import LocalDate from '@booking-services/localDate'
import {omit, minBy, sumBy, min} from 'lodash-es'
import roundMoney from 'root-packages/price-engine/roundMoney.js'


function combinePaymentDates (paymentPlans) {
  const earliestPaymentPlan = minBy(paymentPlans, 'paymentDueDate')
  return paymentPlans.map(paymentPlan => Object.assign(
    omit(paymentPlan, 'combinePaymentDays'),
    {paymentDueDate: earliestPaymentPlan.paymentDueDate
      .differenceInDays(paymentPlan.paymentDueDate) <= earliestPaymentPlan.combinePaymentDays
      ? earliestPaymentPlan.paymentDueDate
      : paymentPlan.paymentDueDate,
    }
  ))
}


function getTourGrossPrice (priceBreakdown) {
  // BSP-3482: foodFunds is included in aggregatedMiscellaneousProducts but won't be counted
  // from there as it's not a proper product type, so must be added to the basic room price
  return priceBreakdown.rooms
    .flatMap(({passengers}) => passengers)
    .reduce((sum, pass) => sum + pass.basicRoomPrice + (pass.foodFunds || 0), 0)
}


function getProductDiscountTotal (productCode, priceBreakdown) {
  return sumBy(
    priceBreakdown
      .discounts
      .promotions
      .applied
      .filter(promo => promo.products.some(product =>
        product.productCode === productCode
      )),
    'amount'
  )
}


function paymentSchedules ({
  departure,
  paymentSchedulePolicies,
  priceBreakdown,
  bookingDate = new LocalDate(),
}) {
  const products = []

  // main tour product
  products.push({
    name: departure.name,
    code: departure.productCode,
    sellingCompanyCode: departure.sellingCompanyCode,
    price: getTourGrossPrice(priceBreakdown) -
      getProductDiscountTotal(departure.productCode, priceBreakdown),
    departureDate: departure.startDate,
    tropicsDepartureId: departure.tropicsDepartureId,
  })

  if (priceBreakdown.extraStays) {
    products.push(
      ...Object.values(priceBreakdown.extraStays)
        .map(({selectedHotel, total}) => ({
          name: selectedHotel.name,
          code: selectedHotel.code,
          sellingCompanyCode: departure.sellingCompanyCode,
          price: total - getProductDiscountTotal(selectedHotel.code, priceBreakdown),
          departureDate: departure.startDate,
        }))
    )
  }

  // insurance & miscellaneous products
  products.push(
    ...priceBreakdown.requestedAdditionalProducts
      .filter(product => product.typeCode === 'IN' || product.typeCode === 'MI')
      .map(({name, productCode, amount}) => ({
        name,
        code: productCode,
        sellingCompanyCode: departure.sellingCompanyCode,
        price: amount - getProductDiscountTotal(productCode, priceBreakdown),
        departureDate: departure.startDate,
      }))
  )


  // aggregated miscellaneous products
  products.push(
    ...priceBreakdown.aggregatedMiscellaneousProducts
      .filter(product => product.typeCode === 'MI')
      .map(({productName, productCode, totalPrice}) => ({
        name: productName,
        code: productCode,
        sellingCompanyCode: departure.sellingCompanyCode,
        price: totalPrice,
        departureDate: departure.startDate,
      }))
  )

  const parseAnyDateOrDateStringToString = (someDate) => {
    return typeof someDate == 'string' ? someDate : someDate.toISOString()
  }

  // Amedeus flights
  if (priceBreakdown.flights) {
    const flights = priceBreakdown.flights.itinerary ||
      priceBreakdown.flights.legs.flatMap(leg =>
        leg.flights.map(flight => ({
          ...flight,
          departDate: flight.departDate.toDate().toISOString(),
        }))
      )
    products.push({
      name: 'Air Product',
      code: 'AIRPROD',
      sellingCompanyCode: departure.sellingCompanyCode,
      price: roundMoney(priceBreakdown.flights.total - getProductDiscountTotal('AIRPROD', priceBreakdown)),
      departureDate: min(flights.map(flight =>
        // strip timezone as we want to operate on the local date/time, not the UTC equivalent
        new LocalDate(parseAnyDateOrDateStringToString(flight.departDate).substring(0, 19)))),
      flights: priceBreakdown.flights,
    })
  }

  let tourFinalPaymentDate
  const paymentSchedule = products
    .filter(product => paymentSchedulePolicies[product.code])
    .flatMap(product => {
      const policy = paymentSchedulePolicies[product.code]
      const productPaymentSchedule = policy.paymentSchedule({
        product,
        numberOfPassengers: priceBreakdown.passengers.length,
        bookingDate,
        departureDate: min(products.map(p => p.departureDate)),
        appliedPromotions: priceBreakdown.discounts.promotions.applied.filter(promo =>
          promo.products.some(prod => prod.productCode === product.code)
        ),
      })

      if (product.code === departure.productCode) {
        let tourFinalPayment = productPaymentSchedule.find(payment => payment.paymentType === 'Final Payment')
        if (tourFinalPayment) {
          tourFinalPaymentDate = tourFinalPayment.paymentDueDate
        }
      }

      return productPaymentSchedule
    })

  const finalPaymentAmount = roundMoney(priceBreakdown.total - sumBy(paymentSchedule, 'paymentAmount'))

  if (finalPaymentAmount > 0) {
    if (!tourFinalPaymentDate && paymentSchedulePolicies.genericMv) {
      const {finalPayment: {daysBeforeDeparture, dueDate}} = paymentSchedulePolicies.genericMv
      tourFinalPaymentDate = min([dueDate, departure.startDate.addDays(-daysBeforeDeparture)])
    }
    if (!tourFinalPaymentDate) {
      tourFinalPaymentDate = bookingDate
    }
    paymentSchedule.push({
      paymentType: 'Final Payment',
      paymentAmount: finalPaymentAmount,
      paymentDueDate: tourFinalPaymentDate,
      products: [],
      combinePaymentDays: paymentSchedulePolicies.genericMv
        ? paymentSchedulePolicies.genericMv.combinePaymentDays
        : 0,
    })
  }

  return combinePaymentDates(paymentSchedule)
}

export default paymentSchedules